import React, { useCallback, useEffect, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { raffleApi } from './raffleApi';
import { downloadCustomerWins, downloadWinners } from './rafflePdf';
import {
  Avatar,
  Notice,
  SectionLabel,
  Sheet,
  SheetTitle,
  Spinner,
} from './RafflePieces';
import {
  AMBER,
  GREEN,
  SLATE,
  formatPhone,
  fullName,
  money,
  pad,
} from './raffleTokens';

const WEEKS = ['This week', 'Last week', '2 weeks ago'];

/**
 * Winners Control — spec §8. The draw is random, so what is left to watch is
 * the claim: who is being paid, and by whom. Counts come from the server's one
 * win calculation, the same one behind the count on the pay step (R61).
 */
export default function WinnersControl({ locationId, locationName, notify }) {
  const [filter, setFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCustomer, setOpenCustomer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await raffleApi.winners(locationId, filter);
    setLoading(false);
    if (!res.success) return notify('error', res.message);
    setData(res);
  }, [locationId, filter, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const types = [{ key: 'all', label: 'All' }, ...(data?.types || [])];
  const filterLabel = types.find((t) => t.key === filter)?.label || 'All';
  const rows = data?.rows || [];

  return (
    <div>
      <div className="rf-xs rf-muted" style={{ marginBottom: 12 }}>
        The draw itself is random, so nobody can pick a winner. This is to watch
        who is claiming the prizes.
      </div>

      <div className="rf-filters">
        {types.map((t) => (
          <button
            key={t.key}
            type="button"
            className="rf-filter"
            data-on={filter === t.key ? '1' : '0'}
            onClick={() => setFilter(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rf-card rf-nopad" style={{ overflow: 'hidden' }}>
        <div className="rf-list-head">
          Last {Math.round((data?.windowDays || 21) / 7)} weeks · tap a customer
          to see every win
        </div>
        {loading && !data && (
          <div className="rf-center rf-pad">
            <Spinner color={SLATE} />
          </div>
        )}
        {data && rows.length === 0 && (
          <div className="rf-pad rf-text-center rf-xs rf-muted">
            No wins in {filterLabel} in the last{' '}
            {Math.round(data.windowDays / 7)} weeks.
          </div>
        )}
        {rows.map((r) => (
          <button
            key={r.customerId}
            type="button"
            className="rf-list-row"
            onClick={() => setOpenCustomer(r.customerId)}
          >
            <Avatar name={r.customerName || ''} size={36} />
            <div className="rf-flex1 rf-minw0 rf-text-left">
              <div className="rf-strong rf-ellipsis">
                {r.customerName || r.customerId}
              </div>
              <div className="rf-xs rf-muted">{money(r.total)} won</div>
            </div>
            {r.flagged && (
              <WarningAmberIcon style={{ color: AMBER, fontSize: 18 }} />
            )}
            <span className="rf-wins" data-high={r.wins >= 3 ? '1' : '0'}>
              <strong>{r.wins}</strong> wins
            </span>
            <ChevronRightIcon style={{ color: SLATE }} />
          </button>
        ))}
        <button
          type="button"
          className="rf-list-foot"
          disabled={!rows.length}
          onClick={() =>
            downloadWinners({
              rows,
              filterLabel,
              windowDays: data.windowDays,
              locationName,
            })
          }
        >
          <DownloadIcon fontSize="small" /> Download winners report
        </button>
      </div>

      {openCustomer && (
        <CustomerWinsSheet
          locationId={locationId}
          locationName={locationName}
          customerId={openCustomer}
          onClose={() => setOpenCustomer(null)}
          notify={notify}
        />
      )}
    </div>
  );
}

function CustomerWinsSheet({
  locationId,
  locationName,
  customerId,
  onClose,
  notify,
}) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    raffleApi.customerWins(locationId, customerId).then((res) => {
      if (!alive) return;
      if (!res.success) {
        notify('error', res.message);
        onClose();
      } else setData(res);
    });
    return () => {
      alive = false;
    };
    // onClose / notify are inline callbacks from the parent; refetching on
    // their identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, customerId]);

  if (!data) {
    return (
      <Sheet onClose={onClose}>
        <div className="rf-center rf-pad">
          <Spinner color={SLATE} size={24} />
        </div>
      </Sheet>
    );
  }

  const c = data.customer;
  const flag = data.concentration;

  return (
    <Sheet onClose={onClose}>
      <SheetTitle onClose={onClose}>
        <span className="rf-row rf-gap-12">
          <Avatar name={fullName(c)} size={44} src={c.photoUrl} />
          <span>
            <span className="rf-h3" style={{ display: 'block' }}>
              {fullName(c)}
            </span>
            <span className="rf-xs rf-muted">{formatPhone(c.phone)}</span>
          </span>
        </span>
      </SheetTitle>

      <div className="rf-row rf-gap-8" style={{ marginBottom: 16 }}>
        <div className="rf-stat">
          <strong>{data.wins.length}</strong>
          <span>wins in {Math.round(data.windowDays / 7)} weeks</span>
        </div>
        <div className="rf-stat">
          <strong style={{ color: GREEN }}>{money(data.total)}</strong>
          <span>total won</span>
        </div>
      </div>

      <div className="rf-row rf-wrap rf-gap-8" style={{ marginBottom: 12 }}>
        {Object.entries(data.byType || {}).map(([t, n]) => (
          <span key={t} className="rf-badge-lav">
            {t} · {n}
          </span>
        ))}
      </div>

      {/* R60 — the single most useful signal on the screen. */}
      {flag && (
        <div style={{ marginBottom: 16 }}>
          <Notice Icon={WarningAmberIcon} tone="amber">
            {flag.count} of {flag.total} wins were paid by the same employee (
            {flag.byName}).
          </Notice>
        </div>
      )}

      {WEEKS.map((wk, i) => {
        const rows = data.wins.filter((w) => w.weeksAgo === i);
        return (
          <div key={wk} style={{ marginBottom: 16 }}>
            <SectionLabel>{wk}</SectionLabel>
            {rows.length === 0 ? (
              <div className="rf-empty-row">No wins</div>
            ) : (
              <div className="rf-card rf-nopad" style={{ overflow: 'hidden' }}>
                {rows.map((w) => (
                  <div key={w.raffleId} className="rf-win-row">
                    <div className="rf-win-tile">
                      <span>{w.mode === 'ticket' ? 'TKT' : 'MCH'}</span>
                      <strong>
                        {w.mode === 'ticket' ? w.value : pad(w.value)}
                      </strong>
                    </div>
                    <div className="rf-flex1 rf-minw0">
                      <div className="rf-strong">{w.type}</div>
                      <div className="rf-xs rf-muted">
                        {w.date} · {w.time}
                        {w.shift ? ` · ${w.shift}` : ''}
                      </div>
                      <div className="rf-xs rf-muted">Paid by {w.byName}</div>
                    </div>
                    <strong style={{ color: GREEN }}>{money(w.amount)}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className="rf-btn rf-btn-navy rf-w100"
        onClick={() =>
          downloadCustomerWins({
            customer: c,
            wins: data.wins,
            total: data.total,
            concentration: flag,
            windowDays: data.windowDays,
            locationName,
          })
        }
      >
        <DownloadIcon fontSize="small" /> Download customer win report
      </button>
    </Sheet>
  );
}
